import type { StateCreator } from 'zustand';
import { compPoints as fetchCompPoints } from '@/api/auth';
import {
  type Bonus,
  BonusStage,
  type Freespin,
  jackpots as fetchJackpots,
  type Jackpot,
  JackpotStage,
  type Lootbox,
  type PlayerTransaction,
  playerBonuses,
  playerFreespins,
  playerLootboxes,
} from '@/api/common';
import { recentGames as getRecentGames, type RecentGame } from '@/api/games';
import { camelize } from '@/helpers/common';
import {
  findActiveLootboxId,
  isWheelLootbox,
  updateBonuses,
  updateRecentsWithNewGame,
} from '@/helpers/realTimeService';
import {
  findDiceLootboxes,
  findLootboxesWithoutTags,
  findScratchLootboxes,
  findWheelLootboxes,
  isDiceLootbox,
  isScratchLootbox,
} from '@/modules/promotions';
import type { CommonStoreType, RealTimeServiceState } from '../types';
import { REASON_FOR_UPDATING_USER } from './auth/helpers';

export const useRealTimeServiceSlice: StateCreator<
  CommonStoreType,
  [],
  [],
  RealTimeServiceState
> = (set, get) => {
  return {
    // jackpots
    activeJackpot: undefined,
    playerJackpotWins: [],
    pendingJackpots: false,
    pendingLootboxes: false,
    setActiveJackpot: async () => {
      try {
        set({ pendingJackpots: true });
        const { data: jackpotsData } = await fetchJackpots();

        const activeJackpot = jackpotsData?.find(
          (jackpot) => jackpot.state === JackpotStage.Active
        );

        if (!activeJackpot) return;

        return set({
          activeJackpot: camelize(activeJackpot) as Jackpot,
        });
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingJackpots: false });
      }
    },
    addPlayerJackpotWin: (jackpotWin) =>
      set({ playerJackpotWins: [...get().playerJackpotWins, jackpotWin] }),

    //lootboxes
    wheelLootboxes: [],
    withoutTagsLootboxes: [],
    scratchLootboxes: [],
    diceLootboxes: [],
    activeLootboxId: undefined,
    dismissedLootboxId: undefined,

    fetchLootboxes: async () => {
      set({ pendingLootboxes: true });
      try {
        const { data: lootboxes } = await playerLootboxes();
        const { activeLootboxId, dismissedLootboxId } = get();

        const wheelLootboxes = findWheelLootboxes(lootboxes);
        const diceLootboxes = findDiceLootboxes(lootboxes);
        const scratchLootboxes = findScratchLootboxes(lootboxes);
        const withoutTagsLootboxes = findLootboxesWithoutTags(lootboxes);

        const newActiveLootboxId = findActiveLootboxId(withoutTagsLootboxes);

        set({
          wheelLootboxes,
          diceLootboxes,
          scratchLootboxes,
          withoutTagsLootboxes,
          ...(!activeLootboxId &&
            newActiveLootboxId &&
            newActiveLootboxId !== dismissedLootboxId && {
              activeLootboxId: newActiveLootboxId,
            }),
        });
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingLootboxes: false });
      }
    },
    updateLootboxes: (lootbox: Lootbox) => {
      if (isWheelLootbox(lootbox)) {
        return set({
          wheelLootboxes: [...get().wheelLootboxes, { ...lootbox, live: true }],
        });
      }

      if (isDiceLootbox(lootbox)) {
        return set({
          diceLootboxes: [...get().diceLootboxes, { ...lootbox, live: true }],
        });
      }

      if (isScratchLootbox(lootbox)) {
        return set({
          scratchLootboxes: [
            ...get().scratchLootboxes,
            { ...lootbox, live: true },
          ],
        });
      }

      set({
        withoutTagsLootboxes: [
          ...get().withoutTagsLootboxes,
          { ...lootbox, live: true },
        ],
      });
    },

    //bonuses
    bonusesPending: false,
    bonuses: [],
    hasUserUsedBonuses: null,
    fetchBonuses: async () => {
      try {
        set({ bonusesPending: true });
        const { data: bonuses = [] } = await playerBonuses();

        const hasUserUsedBonuses = bonuses?.some(({ stage }) =>
          [
            BonusStage.Finished,
            BonusStage.Issued,
            BonusStage.Activated,
          ].includes(stage)
        );

        set({ bonuses, bonusesPending: false, hasUserUsedBonuses });
      } catch (error) {
        console.log(error);
      }
    },
    updateBonuses: (incomingBonus: Bonus) => {
      const { bonuses, user, updatePlayerWallet } = get();

      const updatedBonuses = updateBonuses(bonuses, incomingBonus);

      if (user?.currency) {
        updatePlayerWallet(user?.currency, user);
      }

      set({
        bonuses: updatedBonuses,
      });
    },

    //freespins
    freespinsPending: false,
    freespins: [],
    hasUserUsedFreespins: null,
    fetchFreespins: async () => {
      try {
        set({ freespinsPending: true });
        const { data: freespins = [] } = await playerFreespins();

        const hasUserUsedFreespins = freespins.some(({ stage }) =>
          [
            BonusStage.Finished,
            BonusStage.Issued,
            BonusStage.Activated,
          ].includes(stage)
        );

        set({ freespins, hasUserUsedFreespins });
      } catch (error) {
        console.log(error);
      } finally {
        set({ freespinsPending: false });
      }
    },
    updateFreespins: (incomingFreespin: Freespin) => {
      const { freespins } = get();

      const updatedFreespins = updateBonuses(freespins, incomingFreespin);

      set({
        freespins: updatedFreespins,
      });
    },

    //payments
    playerPayments: [],
    updatePlayerPayments: (payment: PlayerTransaction) => {
      set({
        playerPayments: [...get().playerPayments, payment],
      });
      // DEV-727: temporarily disabled Google Analytics deposit event tracking
      // if (payment.action === 'deposit' && payment.success) {
      //   try {
      //     const deposits = Object.values(
      //       get().lastSuccessfulPaymentsByCurrency || {}
      //     );
      //     sendDepositSuccessEvent({
      //       type: deposits.length > 0 ? 'std' : 'ftd',
      //       amount: payment.amountCents,
      //       currency: payment.currency,
      //     });
      //   } catch (error) {
      //     console.error('Error sending deposit success event:', error);
      //   }
      // }
    },

    //games
    recentGamesIds: [],
    fetchRecentGamesIds: async () => {
      try {
        const { data: recentGames } = await getRecentGames();
        if (!recentGames) return;

        set({
          recentGamesIds: recentGames.map(({ identifier }) => identifier),
        });
      } catch (error) {
        console.log(error);
      }
    },
    updateRecentGames: (recentGame: RecentGame) => {
      set({
        recentGamesIds: updateRecentsWithNewGame(
          get().recentGamesIds,
          recentGame
        ),
      });
    },

    // compPoints
    compPoints: undefined,
    compPointsPending: false,
    fetchCompPoints: async () => {
      try {
        set({ compPointsPending: true });

        const { data: compPoints } = await fetchCompPoints();

        set({ compPoints });

        return;
      } catch (e) {
        console.log(e);
      } finally {
        set({ compPointsPending: false });
      }
    },
    updateCompPoints(incomingCompPoints) {
      set({ compPoints: incomingCompPoints });
    },

    // groups
    updateUserStatuses: (statuses) => {
      const { user, updateUser } = get();
      if (!user) return;

      updateUser({ statuses }, REASON_FOR_UPDATING_USER.REFRESH_STATUSES);
    },
  };
};
